import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './EditMainPage.css';
import { getRandomId, hasPermission, parseApiError, parsePermissions } from '../../../../../../assets/utils/utils';
import { UserContext } from '../../../../../../assets/contexts/userContext';
import useAutoDismissError from '../../../../../../assets/hooks/useAutoDismissError';
import useWindowSize from '../../../../../../assets/hooks/useWindowSize';
import mainApi from '../../../../../../assets/api/MainApi';
import { AddIcon } from '../../../../../../assets/icons/controls/controls';
import { BLOCK_EXEPTION_TYPES, parseType } from '../../../../../../assets/utils/constructor_blocks_constants';
import { PERMISSIONS, RBAC_CONTENT_MANAGEMENT } from '../../../../../../assets/utils/permissions_rbac';

import PreloaderBox from '../../../../../PreloaderBox/PreloaderBox';
import ErrorMessage from '../../../../../ErrorMessage/ErrorMessage';
import { fetchStaticPagesData, saveStaticPageData } from './services/editMainPageApi';
import { ActionsContext } from '../../../../../../assets/contexts/actionsContext';
import { handleChange } from './utils/editMainPageHelpers';
import { TYPE_MAP } from './utils/editMainPageConstants';
import MobileBlocksList from './components/Ui/MobileBlocksList';
import DesktopBlocksList from './components/Ui/DesktopBlocksList';
import BlockSettingsSidebar from './components/Ui/BlockSettingsSidebar';
import EmptyState from './components/Ui/EmptyState';
import BlockFormPopup from './components/Ui/BlockFormPopup';
import BlockPickerPopup from './components/Ui/BlockPickerPopup';
import SaveToast from './components/Ui/SaveToast';
import SubmitActionPopup from '../../../../SubmitActionPopup/SubmitActionPopup';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';





function EditMainPage() {
    const { setActions } = useContext(ActionsContext)

    const { user } = useContext(UserContext)
    const [layout, setLayout] = useState(null)
    const [isSaved, setIsSaved] = useAutoDismissError()
    const [error, showError] = useAutoDismissError()
    const { width } = useWindowSize()
    const isMobile = width && width <= 880

    const [selectedBlockId, setSelectedBlockId] = useState(null)
    const [activeSlideByBlock, setActiveSlideByBlock] = useState({})
    const [isFormPopupOpen, setIsFormPopupOpen] = useState(false)
    const [availableBlocks, setAvailableBlocks] = useState([])
    const [constructorMeta, setConstructorMeta] = useState({
        logo: null,
        icon: null,
        blocks: [],
    })
    const [isConstructorReady, setIsConstructorReady] = useState(false)
    const [blockPicker, setBlockPicker] = useState({
        isOpen: false,
        mode: "insert",
        insertIndex: null,
        targetId: null,
    })
    const [deletePopup, setDeletePopup] = useState({
        isOpen: false,
        index: null,
        title: '',
    })
    const isSavingRef = useRef(false)
    const pendingLayoutRef = useRef(null)
    const lastSavedStructureRef = useRef("")
    const skipNextAutoSaveRef = useRef(false)
    const isAutoSaveReadyRef = useRef(false)
    const lastDataByBlockRef = useRef(new Map())
    const [isDragging, setIsDragging] = useState(false)
    const deletePopupTimeoutRef = useRef(null)

    const [preloaders, setPreloaders] = useState({
        initial: true,
        save: false,
        blocks: false,
    })
    const canEditStructure = user ? hasPermission(parsePermissions(user), [RBAC_CONTENT_MANAGEMENT[PERMISSIONS.EDIT_LAYOUT]]) : false;
    const typeIndexByClientId = useMemo(() => {
        const map = new Map();
        const counters = {};
        if (!layout) return map;
        layout.forEach((item) => {
            const type = item.block?.type;
            if (!type || !item.clientId) return;
            counters[type] = (counters[type] || 0) + 1;
            map.set(item.clientId, counters[type]);
        });
        return map;
    }, [layout]);
    const normalizeBlockType = useCallback((type) => {
        if (!type) return type;
        if (TYPE_MAP[type]) return type;
        const percentVariant = type.replace(/-100$/, '-100%');
        if (TYPE_MAP[percentVariant]) return percentVariant;
        return type;
    }, []);
    const blockTitleByType = useMemo(() => {
        const map = new Map();
        if (!availableBlocks) return map;
        availableBlocks.forEach((block) => {
            if (block?.type) {
                const title = block.title || block.name || block.type;
                map.set(block.type, title);
                const normalizedType = normalizeBlockType(block.type);
                if (normalizedType && normalizedType !== block.type) {
                    map.set(normalizedType, title);
                }
            }
        });
        return map;
    }, [availableBlocks, normalizeBlockType]);
    const getBlockTitle = useCallback((type) => {
        if (!type) return 'Блок';
        const normalizedType = normalizeBlockType(type);
        const fromMap = blockTitleByType.get(normalizedType) || blockTitleByType.get(type);
        if (fromMap) return fromMap;
        const meta = TYPE_MAP[normalizedType] || TYPE_MAP[type];
        if (meta?.title) return meta.title;
        const parsed = parseType(type);
        let base = parsed.baseType.replace(/-/g, ' ');
        base = base.charAt(0).toUpperCase() + base.slice(1);
        let suffix = '';
        if (parsed.lineCount !== undefined) {
            suffix += `: ${parsed.lineCount} ряд`;
        }
        if (parsed.isReverse) {
            suffix += ' (справа)';
        }
        return `${base}${suffix}`;
    }, [blockTitleByType, normalizeBlockType]);
    const isEditableType = useCallback((type) => {
        const normalizedType = normalizeBlockType(type);
        return Boolean(TYPE_MAP[normalizedType]?.form);
    }, [normalizeBlockType]);

    useEffect(() => {
        if (!user) return
        setPreloaders(prevValue => ({
            ...prevValue,
            initial: true,
            blocks: true,
        }))
        const shopId = user?.default_shop?._id;
        Promise.allSettled([
            fetchStaticPagesData(shopId),
            mainApi.getConstructorMainPageBlocks({ shop_id: shopId }),
            mainApi.getConstructorMainPage({ shop_id: shopId }),
        ])
            .then(([layoutResult, blocksResult, metaResult]) => {
                if (layoutResult.status === "fulfilled") {
                    const normalizedLayout = layoutResult.value.map((item) => ({
                        ...item,
                        clientId: getRandomId(),
                    }));
                    setLayout(normalizedLayout);
                } else if (layoutResult.reason?.statusCode === 403) {
                    setLayout(null);
                } else {
                    console.error(layoutResult.reason);
                }

                if (blocksResult.status === "fulfilled") {
                    setAvailableBlocks(blocksResult.value?.data || []);
                } else {
                    console.error(blocksResult.reason);
                }

                if (metaResult.status === "fulfilled") {
                    setConstructorMeta({
                        logo: metaResult.value?.logo || null,
                        icon: metaResult.value?.icon || null,
                        blocks: metaResult.value?.blocks || [],
                    });
                    setIsConstructorReady(true);
                } else {
                    console.error(metaResult.reason);
                }

                if (layoutResult.status === "fulfilled") {
                    mainApi.backupMainPageData({
                        shop_id: shopId,
                        layout: layoutResult.value,
                        meta: metaResult.status === "fulfilled" ? metaResult.value : null,
                    });
                }
            })
            .finally(() => {
                setPreloaders(prevValue => ({
                    ...prevValue,
                    initial: false,
                    blocks: false,
                }));
            });
    }, [user])

    useEffect(() => {
        if (!Array.isArray(layout) || layout.length === 0 || selectedBlockId) return;
        const firstEditable = layout.find((item) => isEditableType(item.block?.type));
        if (firstEditable) {
            setSelectedBlockId(firstEditable.clientId);
        }
    }, [layout, selectedBlockId, isEditableType])

    useEffect(() => {
        if (!preloaders.initial && layout && !isAutoSaveReadyRef.current) {
            isAutoSaveReadyRef.current = true;
            lastSavedStructureRef.current = getStructureSignature(layout);
            skipNextAutoSaveRef.current = true;
        }
    }, [preloaders.initial, layout]);

    useEffect(() => {
        if (!layout) return;
        const nextMap = new Map();
        layout.forEach((item) => {
            if (!item.clientId) return;
            nextMap.set(item.clientId, item.data);
        });
        lastDataByBlockRef.current = nextMap;
    }, [layout]);


    function getStructureSignature(nextLayout) {
        if (!nextLayout) return "";
        return nextLayout.map((item) => getBlockId(item)).filter(Boolean).join("|");
    }

    function getBlockId(item) {
        if (!item) return null;
        return item.block?._id || item.block?.id || item.block_id || item.blockId || null;
    }

    async function performSave(nextLayout, structureChanged) {
        if (!user?.default_shop?._id || !nextLayout) return;
        if (isSavingRef.current) return;
        isSavingRef.current = true;
        setPreloaders(prevValue => ({
            ...prevValue,
            save: true,
        }))
        try {
            const blockIds = nextLayout.map((item) => getBlockId(item)).filter(Boolean);
            const layoutForSave = nextLayout.map((item) => {
                const blockId = getBlockId(item);
                if (!blockId) return item;
                if (!isEditableType(item.block?.type)) {
                    return {
                        ...item,
                        block: {
                            ...(item.block || {}),
                            _id: blockId,
                        },
                        data: null,
                    };
                }
                const preservedData = Array.isArray(item.data) && item.data.length > 0
                    ? item.data
                    : (lastDataByBlockRef.current.get(item.clientId) ?? item.data);
                const shouldNullData = !isEditableType(item.block?.type);
                return {
                    ...item,
                    block: {
                        ...(item.block || {}),
                        _id: blockId,
                    },
                    data: shouldNullData ? null : preservedData,
                };
            });
            if (structureChanged) {
                await mainApi.setConstructorMainPage({
                    shop_id: user?.default_shop?._id,
                    logo: constructorMeta.logo?._id || null,
                    icon: constructorMeta.icon?._id || null,
                    blocks: blockIds,
                });
                await saveStaticPageData(user?.default_shop?._id, layoutForSave);
                lastSavedStructureRef.current = getStructureSignature(nextLayout);
            } else {
                await saveStaticPageData(user?.default_shop?._id, layoutForSave);
            }
            setIsSaved(true);
        } catch (err) {
            console.error(err);
            showError(parseApiError(err));
        } finally {
            setPreloaders(prevValue => ({
                ...prevValue,
                save: false,
            }));
            isSavingRef.current = false;
        }
        if (pendingLayoutRef.current) {
            const pendingLayout = pendingLayoutRef.current;
            pendingLayoutRef.current = null;
            const nextSignature = getStructureSignature(pendingLayout);
            const pendingStructureChanged = nextSignature !== lastSavedStructureRef.current;
            performSave(pendingLayout, pendingStructureChanged);
        }
    }

    function requestSave(nextLayout) {
        if (!isAutoSaveReadyRef.current) return;
        if (!nextLayout) return;
        if (skipNextAutoSaveRef.current) {
            skipNextAutoSaveRef.current = false;
            return;
        }
        const nextSignature = getStructureSignature(nextLayout);
        const structureChanged = nextSignature !== lastSavedStructureRef.current;
        if (isSavingRef.current) {
            pendingLayoutRef.current = nextLayout;
            return;
        }
        performSave(nextLayout, structureChanged);
    }

    useEffect(() => {
        if (!isAutoSaveReadyRef.current) return;
        if (!layout) return;
        requestSave(layout);
    }, [layout, constructorMeta, isConstructorReady]);

    useEffect(() => {
        setActions([]);
    }, [setActions])

    function handleInlineChange({ item, index, subname, value }) {
        handleChange({
            target: {
                item,
                index,
                value,
                name: item.block.type,
                subname,
            }
        }, setLayout, showError);
    }

    function getImageUrl(imageList) {
        if (!imageList || imageList.length === 0) return null;
        const image = imageList[0];
        return image?.urls?.orig || image?.urls?.medium || image?.url || null;
    }

    function setActiveSlide(itemId, index) {
        setActiveSlideByBlock(prevValue => ({
            ...prevValue,
            [itemId]: index,
        }));
    }

    function openFormPopup(blockId) {
        const block = layout?.find((item) => item.clientId === blockId);
        if (!block || !isEditableType(block.block?.type)) return;
        setSelectedBlockId(blockId);
        setIsFormPopupOpen(true);
    }

    function handleCloseFormPopup() {
        setIsFormPopupOpen(false);
    }

    function openBlockPicker({ mode, insertIndex, targetId }) {
        if (!canEditStructure) return;
        setBlockPicker({
            isOpen: true,
            mode,
            insertIndex: insertIndex ?? null,
            targetId: targetId ?? null,
        });
    }

    function closeBlockPicker() {
        setBlockPicker({
            isOpen: false,
            mode: "insert",
            insertIndex: null,
            targetId: null,
        });
    }

    function createDefaultBlockData() {
        return {
            _id: getRandomId(),
            title: "",
            text: "",
            link: "",
            btn_text: "",
            image: [],
            image_tablet: [],
            image_mobile: [],
            products: null,
        };
    }

    function createLayoutItem(block) {
        return {
            _id: getRandomId(),
            clientId: getRandomId(),
            block,
            data: [createDefaultBlockData()],
        };
    }

    function handleSelectBlock(block) {
        if (!canEditStructure) return;
        if (blockPicker.mode === "replace" && blockPicker.targetId) {
            const newClientId = getRandomId();
            setLayout((prevValue) => {
                if (!prevValue) return prevValue;
                return prevValue.map((item) => {
                    if (item.clientId !== blockPicker.targetId) return item;
                    return {
                        ...item,
                        clientId: newClientId,
                        block,
                        data: [createDefaultBlockData()],
                    };
                });
            });
            setSelectedBlockId(newClientId);
            closeBlockPicker();
            return;
        }

        const newItem = createLayoutItem(block);
        setLayout((prevValue) => {
            const next = prevValue ? [...prevValue] : [];
            const insertIndex = blockPicker.insertIndex === null ? next.length : blockPicker.insertIndex;
            next.splice(insertIndex, 0, newItem);
            return next;
        });
        setSelectedBlockId(newItem.clientId);
        closeBlockPicker();
    }

    function deleteBlock(index) {
        if (!canEditStructure) return;
        setLayout((prevValue) => {
            if (!prevValue || prevValue.length === 0) return prevValue;
            const removed = prevValue[index];
            const next = prevValue.filter((_, i) => i !== index);
            if (removed && removed.clientId === selectedBlockId) {
                const nextSelected = next[index]?.clientId || next[index - 1]?.clientId || null;
                setSelectedBlockId(nextSelected);
            }
            return next;
        });
    }

    function openDeletePopup(index, title) {
        if (!canEditStructure) return;
        setDeletePopup({
            isOpen: true,
            index,
            title: title || 'Блок',
        });
    }

    function closeDeletePopup() {
        if (deletePopupTimeoutRef.current) {
            clearTimeout(deletePopupTimeoutRef.current);
        }
        setDeletePopup({
            isOpen: false,
            index: null,
            title: deletePopup.title,
        });
        deletePopupTimeoutRef.current = setTimeout(() => {
            setDeletePopup((prev) => ({
                ...prev,
                title: '',
            }));
            deletePopupTimeoutRef.current = null;
        }, 300);
    }

    function handleConfirmDelete() {
        if (deletePopup.index === null) return;
        deleteBlock(deletePopup.index);
        closeDeletePopup();
    }

    function moveBlock(index, direction) {
        if (!canEditStructure) return;
        setLayout((prevValue) => {
            if (!prevValue || prevValue.length === 0) return prevValue;
            const next = [...prevValue];
            if (direction === "up" && index > 0) {
                [next[index - 1], next[index]] = [next[index], next[index - 1]];
            }
            if (direction === "down" && index < next.length - 1) {
                [next[index + 1], next[index]] = [next[index], next[index + 1]];
            }
            return next;
        });
    }

    function handleDragEnd(result) {
        if (!canEditStructure) {
            setIsDragging(false);
            return;
        }
        const { source, destination } = result;
        if (!destination) {
            setIsDragging(false);
            return;
        }
        if (destination.index === source.index) {
            setIsDragging(false);
            return;
        }
        setLayout((prevValue) => {
            if (!prevValue) return prevValue;
            const next = [...prevValue];
            const [removed] = next.splice(source.index, 1);
            next.splice(destination.index, 0, removed);
            return next;
        });
        setIsDragging(false);
    }

    function renderInsertBar(index) {
        if (!canEditStructure) return null;
        if (isMobile) return null;
        return (
            <button
                className='edit-main-page__insert'
                type='button'
                onClick={(e) => {
                    e.stopPropagation();
                    openBlockPicker({ mode: "insert", insertIndex: index });
                }}
            >
                <span className='edit-main-page__insert-line' />
                <span className='edit-main-page__insert-plus'>
                    <AddIcon
                        mainClassName={'edit-main-page__insert-plus-icon'}
                        fillClassName={'edit-main-page__insert-plus-icon-fill'}
                    />
                </span>
            </button>
        );
    }

    function openFormPopup(blockId) {
        setSelectedBlockId(blockId);
        setIsFormPopupOpen(true);
    }

    function handleCloseFormPopup() {
        setIsFormPopupOpen(false);
    }

    function renderPromoBannerPreview(item, title, isSelected, isScroll) {
        const slides = item.data || [];
        const savedIndex = activeSlideByBlock[item.clientId] || 0;
        const activeIndex = Math.min(savedIndex, Math.max(slides.length - 1, 0));

        function renderSlide(slide, index) {
            const imageUrl = getImageUrl(slide?.image);
            const isActive = index === activeIndex;
            return (
                <div className='edit-main-page__preview-banner-image'>
                    {imageUrl ?
                        <img src={imageUrl} alt="" />
                        :
                        <div className='edit-main-page__preview-placeholder edit-main-page__preview-placeholder_banner'>
                            <div className='edit-main-page__preview-placeholder-title'>Баннер</div>
                            <div className='edit-main-page__preview-placeholder-text'>Загрузите изображение для промо</div>
                        </div>}
                    {slide?.title !== undefined && isActive ?
                        <div className='edit-main-page__preview-banner-overlay'>
                            <div className='edit-main-page__preview-label'>Заголовок баннера</div>
                            <textarea
                                className={`edit-main-page__preview-title-input ${isSelected ? 'edit-main-page__preview-title-input_editable' : ''}`}
                                rows={2}
                                placeholder='Введите заголовок'
                                value={slide.title || ''}
                                onChange={(e) => handleInlineChange({
                                    item,
                                    index: activeIndex,
                                    subname: 'title',
                                    value: e.target.value,
                                })}
                            />
                        </div>
                        : null}
                </div>
            );
        }

        return (
            <div className='edit-main-page__preview-banner'>
                {slides.length > 1 ?
                    <Swiper
                        className={`edit-main-page__preview-swiper ${isScroll ? 'edit-main-page__preview-swiper_scroll' : ''}`}
                        modules={[Pagination]}
                        slidesPerView={isScroll ? 'auto' : 1}
                        spaceBetween={isScroll ? 12 : 0}
                        centeredSlides={Boolean(isScroll)}
                        pagination={{ clickable: true }}
                        onSlideChange={(swiper) => {
                            setActiveSlide(item.clientId, swiper.realIndex ?? swiper.activeIndex);
                        }}
                    >
                        {slides.map((slide, idx) => (
                            <SwiperSlide key={`${item.clientId}_slide_${idx}`} className='edit-main-page__preview-slide'>
                                {renderSlide(slide, idx)}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    :
                    renderSlide(slides[activeIndex] || {}, activeIndex)}
                <div className='edit-main-page__preview-meta'>
                    {/* <span>{title}</span> */}
                    <span>Слайды: {slides.length || 0}</span>
                </div>
            </div>
        );
    }

    function renderPromoTextPreview(item, title, reversed, isSelected) {
        const data = item.data?.[0] || {};
        const imageUrl = getImageUrl(data.image);
        return (
            <div className={`edit-main-page__preview-text ${reversed ? 'edit-main-page__preview-text_reverse' : ''}`}>
                <div className='edit-main-page__preview-text-content'>
                    {data.title !== undefined ?
                        <textarea
                            className={`edit-main-page__preview-title-input ${isSelected ? 'edit-main-page__preview-title-input_editable' : ''}`}
                            rows={2}
                            placeholder='Заголовок'
                            value={data.title || ''}
                            onChange={(e) => handleInlineChange({
                                item,
                                index: 0,
                                subname: 'title',
                                value: e.target.value,
                            })}
                        />
                        : null}
                    {data.text !== undefined ?
                        <textarea
                            className={`edit-main-page__preview-textarea ${isSelected ? 'edit-main-page__preview-textarea_editable' : ''}`}
                            rows={3}
                            placeholder='Текст'
                            value={data.text || ''}
                            onChange={(e) => handleInlineChange({
                                item,
                                index: 0,
                                subname: 'text',
                                value: e.target.value,
                            })}
                        />
                        : null}
                    {data.btn_text !== undefined ?
                        <input
                            className={`edit-main-page__preview-button ${isSelected ? 'edit-main-page__preview-button_editable' : ''}`}
                            placeholder='Текст кнопки'
                            value={data.btn_text || ''}
                            onChange={(e) => handleInlineChange({
                                item,
                                index: 0,
                                subname: 'btn_text',
                                value: e.target.value,
                            })}
                        />
                        : null}
                    {/* <div className='edit-main-page__preview-meta'>
                        <span>{title}</span>
                    </div> */}
                </div>
                <div className='edit-main-page__preview-text-image'>
                    {imageUrl ?
                        <img src={imageUrl} alt="" />
                        :
                        <div className='edit-main-page__preview-placeholder edit-main-page__preview-placeholder_image'>
                            <div className='edit-main-page__preview-placeholder-title'>Изображение</div>
                            <div className='edit-main-page__preview-placeholder-text'>Добавьте фото для блока</div>
                        </div>}
                </div>
            </div>
        );
    }

    function renderShowcasePreview(item, title, isSelected) {
        const data = item.data?.[0] || {};
        const products = data.products || [];
        const limit = item.block?.input_data?.products?.limit || 0;
        const placeholderCount = Math.max(limit - products.length, 0);
        const canEditTitle = item.block?.input_data?.title?.editable;
        const canEditText = item.block?.input_data?.text?.editable;
        const hasBanner = Boolean(item.block?.input_data?.image);
        const bannerUrl = getImageUrl(data.image);

        return (
            <div className='edit-main-page__preview-showcase'>
                <div className='edit-main-page__preview-showcase-header'>
                    {canEditTitle ?
                        <textarea
                            className={`edit-main-page__preview-title-input ${isSelected ? 'edit-main-page__preview-title-input_editable' : ''}`}
                            rows={2}
                            placeholder='Заголовок витрины'
                            value={data.title || ''}
                            onChange={(e) => handleInlineChange({
                                item,
                                index: 0,
                                subname: 'title',
                                value: e.target.value,
                            })}
                        />
                        : null}
                    {canEditText ?
                        <textarea
                            className={`edit-main-page__preview-textarea ${isSelected ? 'edit-main-page__preview-textarea_editable' : ''}`}
                            rows={3}
                            placeholder='Текст'
                            value={data.text || ''}
                            onChange={(e) => handleInlineChange({
                                item,
                                index: 0,
                                subname: 'text',
                                value: e.target.value,
                            })}
                        />
                        : null}
                </div>
                {hasBanner ?
                    <div className='edit-main-page__preview-banner'>
                        <div className='edit-main-page__preview-banner-image'>
                            {bannerUrl ?
                                <img src={bannerUrl} alt="" />
                                :
                                <div className='edit-main-page__preview-placeholder edit-main-page__preview-placeholder_banner'>
                                    <div className='edit-main-page__preview-placeholder-title'>Баннер</div>
                                    <div className='edit-main-page__preview-placeholder-text'>Загрузите изображение для промо</div>
                                </div>}
                        </div>
                    </div>
                    : null}
                <div className='edit-main-page__preview-showcase-grid'>
                    {products.map((product) => {
                        const previewUrl = getImageUrl(product.files);
                        return (
                            <div className='edit-main-page__preview-product' key={product._id || product.name}>
                                {previewUrl ?
                                    <img src={previewUrl} alt="" />
                                    :
                                    <div className='edit-main-page__preview-product-placeholder'>
                                        <span>Нет фото</span>
                                    </div>}
                            </div>
                        );
                    })}
                    {Array.from({ length: placeholderCount }).map((_, idx) => (
                        <div className='edit-main-page__preview-product edit-main-page__preview-product_empty' key={`placeholder_${idx}`}>
                            {products.length + idx + 1}
                        </div>
                    ))}
                </div>
                <div className='edit-main-page__preview-meta'>
                    {/* <span>{title}</span> */}
                    <span>Выберите товары через кнопку «Редактировать»</span>
                </div>
            </div>
        );
    }

    function renderUnknownPreview(title) {
        return (
            <div className='edit-main-page__preview-generic'>
                <div className='edit-main-page__preview-generic-body'>
                    <div className='edit-main-page__preview-generic-line' />
                    <div className='edit-main-page__preview-generic-line edit-main-page__preview-generic-line_short' />
                    <div className='edit-main-page__preview-generic-block' />
                </div>
                <div className='edit-main-page__preview-meta'>
                    {/* <span>{title}</span> */}
                    <span>Для этого блока нет настроек</span>
                </div>
            </div>
        );
    }

    function renderPreview(item, title, isSelected) {
        const blockType = item.block?.type || item.block;
        if (!isEditableType(blockType)) {
            return renderUnknownPreview(title);
        }
        switch (blockType) {
            case 'promo-banner-100%':
                return renderPromoBannerPreview(item, title, isSelected, false);
            case 'promo-banner-scroll':
                return renderPromoBannerPreview(item, title, isSelected, true);
            case 'promo-text-image':
                return renderPromoTextPreview(item, title, true, isSelected);
            case 'promo-text-image-reverse':
                return renderPromoTextPreview(item, title, false, isSelected);
            default:
                return renderShowcasePreview(item, title, isSelected);
        }
    }

    const selectedBlock = useMemo(() => {
        if (!Array.isArray(layout) || !selectedBlockId) return null;
        return layout.find((item) => item.clientId === selectedBlockId) || null;
    }, [layout, selectedBlockId]);

    const selectedTypeMeta = selectedBlock ? TYPE_MAP[selectedBlock.block.type] : null;
    const SelectedFormComponent = selectedTypeMeta ? selectedTypeMeta.form : null;
    const selectableBlocks = useMemo(() => {
        if (!availableBlocks || availableBlocks.length === 0) return [];
        return availableBlocks
            .filter((item) => BLOCK_EXEPTION_TYPES.indexOf(item.type) < 0)
            .sort((a, b) => {
                const aType = parseType(a.type);
                const bType = parseType(b.type);

                if (aType.baseType < bType.baseType) return -1;
                if (aType.baseType > bType.baseType) return 1;

                if (aType.lineCount !== undefined && bType.lineCount !== undefined) {
                    return aType.lineCount - bType.lineCount;
                }

                if (aType.lineCount === undefined && bType.lineCount !== undefined) return -1;
                if (aType.lineCount !== undefined && bType.lineCount === undefined) return 1;

                if (!aType.isReverse && bType.isReverse) return -1;
                if (aType.isReverse && !bType.isReverse) return 1;

                return 0;
            });
    }, [availableBlocks]);
    return (
        <div className='edit-main-page'>
            <SubmitActionPopup
                isOpen={deletePopup.isOpen}
                hendleClose={closeDeletePopup}
                hendleSubmit={handleConfirmDelete}
                text={`Вы уверены что хотите удалить блок "${deletePopup.title}"?`}
                submitText={'Удалить'}
                cencelText={'Отменить'}
            />
            <ErrorMessage error={error} />
            {!preloaders.initial ?
                <div className='edit-main-page__preview'>
                    {layout && layout.length > 0 ?
                        isMobile ?
                            <MobileBlocksList
                                layout={layout}
                                typeIndexByClientId={typeIndexByClientId}
                                selectedBlockId={selectedBlockId}
                                onOpenForm={openFormPopup}
                                onMoveBlock={moveBlock}
                                onOpenBlockPicker={openBlockPicker}
                                onDeleteBlock={openDeletePopup}
                                renderPreview={renderPreview}
                                getBlockTitle={getBlockTitle}
                                isEditableType={isEditableType}
                                canEditStructure={canEditStructure}
                            />
                            :
                            <div className='edit-main-page__workspace'>
                                <div className='edit-main-page__canvas'>
                                    <DesktopBlocksList
                                        layout={layout}
                                        typeIndexByClientId={typeIndexByClientId}
                                        selectedBlockId={selectedBlockId}
                                        isDragging={isDragging}
                                        onDragStart={() => setIsDragging(true)}
                                        onDragEnd={handleDragEnd}
                                        onSelectBlock={setSelectedBlockId}
                                        onOpenBlockPicker={openBlockPicker}
                                        onDeleteBlock={openDeletePopup}
                                        renderInsertBar={renderInsertBar}
                                        renderPreview={renderPreview}
                                        getBlockTitle={getBlockTitle}
                                        isEditableType={isEditableType}
                                        canEditStructure={canEditStructure}
                                    />
                                </div>
                                <BlockSettingsSidebar
                                    selectedBlock={selectedBlock}
                                    selectedTypeMeta={selectedTypeMeta}
                                    SelectedFormComponent={SelectedFormComponent}
                                    onChange={(input) => handleChange(input, setLayout, showError)}
                                />
                            </div>
                        :
                        Array.isArray(layout) ?
                            <EmptyState
                                onAdd={() => openBlockPicker({ mode: "insert", insertIndex: 0 })}
                                text="Пока нет блоков на главной странице магазина."
                                buttonText="Добавить первый блок"
                                canEditStructure={canEditStructure}
                            />
                            : null}
                </div>
                :
                <PreloaderBox />
            }

            {isMobile ?
                <BlockFormPopup
                    isOpen={isFormPopupOpen}
                    onClose={handleCloseFormPopup}
                    selectedTypeMeta={selectedTypeMeta}
                    SelectedFormComponent={SelectedFormComponent}
                    selectedBlock={selectedBlock}
                    onChange={(input) => handleChange(input, setLayout, showError)}
                />
                : null}
            <BlockPickerPopup
                isOpen={blockPicker.isOpen}
                onClose={closeBlockPicker}
                selectableBlocks={selectableBlocks}
                onSelectBlock={handleSelectBlock}
            />
            <SaveToast isSaving={preloaders.save} />
        </div>
    );
}

export default EditMainPage
